import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import RichTextEditor from "@/components/site/RichTextEditor";
import MediaEmbed from "@/components/site/MediaEmbed";

const EMPTY_FORM = { question: "", answer: "", tags: "", category: "", pinned: false, active: true, media_url: "" };

export default function FAQAdminTab() {
  const [faqs, setFaqs] = React.useState([]);
  const [themes, setThemes] = React.useState([]);
  const [showForm, setShowForm] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_FORM);

  const load = React.useCallback(() => {
    api.get("/faq").then((r) => setFaqs(r.data)).catch(() => {});
    api.get("/site-config").then((r) => {
      const t = r.data?.categories?.faq_themes || [];
      setThemes(t);
      setForm((prev) => prev.category ? prev : { ...prev, category: t[0] || "" });
    }).catch(() => {});
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const reset = () => setForm({ ...EMPTY_FORM, category: themes[0] || "" });

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    if (!payload.media_url) payload.media_url = null;
    try {
      if (editing) await api.patch(`/admin/faq/${editing}`, payload);
      else await api.post("/admin/faq", payload);
      toast.success(editing ? "Fiche mise à jour" : "Fiche créée");
      setShowForm(false);
      setEditing(null);
      reset();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const edit = (f) => {
    setEditing(f.id);
    setForm({
      question: f.question,
      answer: f.answer || "",
      tags: (f.tags || []).join(", "),
      category: f.category || (themes[0] || ""),
      pinned: !!f.pinned,
      active: f.active !== false,
      media_url: f.media_url || "",
    });
    setShowForm(true);
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette fiche FAQ ?")) return;
    await api.delete(`/admin/faq/${id}`);
    toast.success("Supprimée");
    load();
  };

  return (
    <div className="grid gap-8">
      <section>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-display font-black text-2xl tracking-tighter uppercase">FAQ vérifiée — {faqs.length}</h2>
          <button
            onClick={() => { setShowForm((s) => !s); setEditing(null); reset(); }}
            className="neo-btn bg-cef-blue text-white"
            data-testid="new-faq-btn"
          >
            <Plus className="w-4 h-4" /> Nouvelle question
          </button>
        </div>
        <p className="text-sm text-neutral-600 mb-3">
          Les thématiques sont gérées dans <strong>Configuration du site → Thématiques FAQ</strong>. Les questions sont groupées par thématique sur la page publique <em>Comprendre / Tes droits</em>.
        </p>
        {showForm && (
          <form onSubmit={submit} className="neo-card p-5 mb-6 grid sm:grid-cols-2 gap-3" data-testid="faq-form">
            <input className="neo-input sm:col-span-2" placeholder="Question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} required data-testid="faq-question" />

            <div className="sm:col-span-2 grid gap-1">
              <label className="text-xs font-bold uppercase tracking-widest">Réponse (mise en forme, liens, médias)</label>
              <RichTextEditor value={form.answer} onChange={(html) => setForm({ ...form, answer: html })} placeholder="Rédige ta réponse…" />
              <p className="text-xs text-neutral-500">Tu peux mettre en gras / italique / souligné, insérer des liens, ajouter des titres, listes, images et vidéos YouTube via la barre d'outils.</p>
            </div>

            <div className="sm:col-span-2 grid gap-1">
              <label className="text-xs font-bold uppercase tracking-widest">Vidéo en option (YouTube · TikTok · Instagram · MP4)</label>
              <input
                className="neo-input"
                placeholder="https://youtu.be/… ou https://www.tiktok.com/@… ou https://www.instagram.com/reel/…"
                value={form.media_url}
                onChange={(e) => setForm({ ...form, media_url: e.target.value })}
                data-testid="faq-media-url"
              />
              {form.media_url && (
                <div className="mt-2">
                  <div className="text-xs font-bold text-neutral-500 mb-1">Aperçu :</div>
                  <MediaEmbed url={form.media_url} />
                </div>
              )}
            </div>

            <input className="neo-input" placeholder="Tags (séparés par , )" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="faq-tags" />
            <select className="neo-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="faq-category">
              {themes.length === 0 && <option value="">Aucune thématique — configure-les d'abord</option>}
              {themes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} data-testid="faq-pinned" /> Épingler en haut</label>
            <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="faq-active" /> Active (visible sur le site)</label>
            <button type="submit" className="neo-btn bg-cef-pink text-white sm:col-span-2" data-testid="faq-submit">{editing ? "Enregistrer" : "Créer la fiche"}</button>
          </form>
        )}
        <div className="grid gap-3" data-testid="faq-admin-list">
          {faqs.map((f) => (
            <div key={f.id} className="neo-card p-4" data-testid={`faq-admin-${f.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2">
                    {f.pinned && <span className="chip bg-cef-pink text-white text-[10px] py-0.5">épinglé</span>}
                    {f.active === false && <span className="chip bg-neutral-300 text-[10px] py-0.5">inactif</span>}
                    {f.media_url && <span className="chip bg-cef-blue text-white text-[10px] py-0.5">📹 média</span>}
                    <div className="font-bold">{f.question}</div>
                  </div>
                  <p className="mt-1 text-sm text-neutral-700 line-clamp-3">{String(f.answer || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}</p>
                  <div className="mt-2 flex flex-wrap gap-1 text-xs">
                    {f.category && <span className="chip bg-cef-yellow">{f.category}</span>}
                    {(f.tags || []).map((t) => <span key={t} className="chip bg-white">#{t}</span>)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => edit(f)} className="neo-btn bg-cef-yellow text-sm py-2" data-testid={`edit-faq-${f.id}`}>Éditer</button>
                  <button onClick={() => del(f.id)} className="neo-btn bg-white text-sm py-2" data-testid={`del-faq-${f.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
